import React from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getExpandedRowModel,
  flexRender,
  SortingState,
  ColumnFiltersState,
  ExpandedState,
  RowSelectionState,
  type CellContext,
  type ColumnDef,
  type HeaderContext,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Component, TableConfig, TableColumn, Viewport } from "@/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useDataWriteback } from "@/hooks/useDataWriteback";
import {
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronDownIcon,
  ChevronRightIcon,
  AlertCircle,
  Plus,
} from "lucide-react";
import { TableLoadingSkeleton } from "@/components/common/DataLoadingSkeletons";
import { useQueryTemplateData, isQueryTemplateDataSource } from '@/hooks/useQueryTemplateData';
import { useComponentData } from '@/stores/editor/component-data-store';
import { TableActionsCell } from "./TableActionsCell";
import { getVisibleTableColumns } from "./table-responsive-columns";
import {
  TableRowDataEditor,
} from "./TableRowDataEditor";
import { useTableModalPreview } from "./table-modal-preview-context";
import { createTableRowEditorFields } from "./table-row-editor-fields";
import {
  canMutateLocalTableRows,
  commitLocalTableRows,
  generateLocalTableRowId,
  getTableRowRecordId,
  getTableRowWritebackConfig,
} from "./table-local-row-mutations";

interface AdvancedTableProps {
  component: Component;
  viewport?: Viewport;
  mobileViewport?: boolean;
}

const defaultColumns: TableColumn[] = [];

const defaultData: Record<string, unknown>[] = [];

const mobileAdvancedTableColors = {
  border: "#363B47",
  surfacePrimary: "#272B34",
  surfaceSecondary: "#1C1F26",
  textPrimary: "#ECEEF2",
  textSecondary: "#9AA2B1",
  textTertiary: "#8A93A5",
  textDisabled: "#8892A3",
} as const;

const mobileAdvancedTableSemanticColors: Record<string, string> = {
  "--background": "#272B34",
  "--foreground": "#EDEFF3",
  "--card": "#31353F",
  "--border": "#4D5460",
  "--input": "#4D5460",
  "--muted-foreground": "#98A0AE",
  "--surface-primary": "#272B34",
  "--surface-secondary": "#383C47",
  "--text-primary": "#EDEFF3",
  "--text-secondary": "#98A0AE",
  "--text-tertiary": "#757D8A",
  "--text-disabled": "#4D5460",
} as const;

type TableRowData = Record<string, unknown> & {
  id?: string;
  subRows?: TableRowData[];
  data?: Record<string, unknown>;
};

type TableStyle = React.CSSProperties & {
  "--tm-column-width"?: string;
  "--tm-row-hover"?: string;
  "--tm-table-min-width"?: string;
};

type RowEditorState =
  | {
      mode: "create";
      rowData: TableRowData;
    }
  | {
      mode: "edit";
      rowIndex: number;
      rowData: TableRowData;
    };

function getStyleRecord(value: unknown): Record<string, unknown> | undefined {
  return isRecord(value) ? value : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isTableRowData(value: unknown): value is TableRowData {
  return isRecord(value);
}

function isTableColumn(value: unknown): value is TableColumn {
  return (
    isRecord(value) &&
    (value.id === undefined || typeof value.id === "string") &&
    typeof value.accessorKey === "string" &&
    typeof value.header === "string"
  );
}

function normalizeTableRow(row: TableRowData): TableRowData {
  if (!isRecord(row.data)) {
    return row;
  }

  const { data, ...rest } = row;
  return {
    ...data,
    ...rest,
  };
}

function getTableRows(value: unknown): TableRowData[] {
  return Array.isArray(value)
    ? value.filter(isTableRowData).map(normalizeTableRow)
    : [];
}

function getTableColumns(value: unknown): TableColumn[] {
  return Array.isArray(value) ? value.filter(isTableColumn) : [];
}

function getFontSize(value: unknown): string | number | undefined {
  return typeof value === "string" || typeof value === "number"
    ? value
    : undefined;
}

function getFilterInputValue(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function getRowId(row: TableRowData, fallbackId: string): string {
  return getTableRowRecordId(row) ?? fallbackId;
}

function isInteractiveTableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;

  const interactiveElement = target.closest("button,input,select,textarea,a,[role='button']");
  if (!(interactiveElement instanceof HTMLElement)) return false;

  const rowElement = target.closest("[data-table-row-index]");
  if (rowElement instanceof HTMLElement) {
    return rowElement.contains(interactiveElement);
  }

  return true;
}

function getDelegatedTableRowIndex(target: EventTarget | null): number | null {
  if (!(target instanceof HTMLElement)) return null;

  const rowElement = target.closest("[data-table-row-index]");
  if (!(rowElement instanceof HTMLElement)) return null;

  const rowIndex = Number(rowElement.dataset.tableRowIndex);
  return Number.isInteger(rowIndex) && rowIndex >= 0 ? rowIndex : null;
}

function extractTableContainerStyle(
  bag: Record<string, unknown> | undefined,
): React.CSSProperties {
  if (!bag) return {};

  const result: React.CSSProperties = {};
  const stringProps = [
    "background",
    "backgroundColor",
    "border",
    "borderTop",
    "borderRight",
    "borderBottom",
    "borderLeft",
    "borderColor",
    "borderStyle",
    "boxShadow",
    "overflow",
    "overflowX",
    "overflowY",
  ] as const;
  const numOrStringProps = [
    "padding",
    "paddingTop",
    "paddingRight",
    "paddingBottom",
    "paddingLeft",
    "borderRadius",
    "borderWidth",
    "opacity",
  ] as const;

  for (const key of stringProps) {
    const value = bag[key];
    if (typeof value === "string" && value.length > 0) {
      Object.assign(result, { [key]: value });
    }
  }

  for (const key of numOrStringProps) {
    const value = bag[key];
    if (typeof value === "string" || typeof value === "number") {
      Object.assign(result, { [key]: value });
    }
  }

  return result;
}

function resolveMobileAdvancedTableColorString(value: string): string {
  const trimmed = value.trim();
  const variableMatch = trimmed.match(/^hsl\(var\((--[a-z-]+)\)\)$/);
  if (variableMatch) {
    return mobileAdvancedTableSemanticColors[variableMatch[1]] ?? value;
  }

  const directVariableMatch = trimmed.match(/^var\((--[a-z-]+)\)$/);
  if (directVariableMatch) {
    return mobileAdvancedTableSemanticColors[directVariableMatch[1]] ?? value;
  }

  return value;
}

function resolveMobileAdvancedTableColor<Value>(value: Value): Value | string {
  return typeof value === "string"
    ? resolveMobileAdvancedTableColorString(value)
    : value;
}

function normalizeMobileAdvancedTableStyle(
  tableStyle: React.CSSProperties,
): React.CSSProperties {
  const normalized: React.CSSProperties = { ...tableStyle };
  const background = resolveMobileAdvancedTableColor(tableStyle.background);
  const backgroundColor = resolveMobileAdvancedTableColor(tableStyle.backgroundColor);
  const borderColor = resolveMobileAdvancedTableColor(tableStyle.borderColor);
  const color = resolveMobileAdvancedTableColor(tableStyle.color);

  if (background === undefined) {
    delete normalized.background;
  } else {
    normalized.background = background;
  }

  if (backgroundColor === undefined) {
    delete normalized.backgroundColor;
  } else {
    normalized.backgroundColor = backgroundColor;
  }

  if (borderColor === undefined) {
    delete normalized.borderColor;
  } else {
    normalized.borderColor = borderColor;
  }

  if (color === undefined) {
    delete normalized.color;
  } else {
    normalized.color = color;
  }

  return normalized;
}

const normalizeAccessorToken = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const findAccessorKey = (row: Record<string, unknown>, accessorKey: string): string | undefined => {
  if (accessorKey in row) return accessorKey;

  const rowKeys = Object.keys(row);
  const lowerAccessorKey = accessorKey.toLowerCase();
  const exactKey = rowKeys.find(key => key.toLowerCase() === lowerAccessorKey);
  if (exactKey) return exactKey;

  const aggregatePrefixes = ['last_', 'avg_', 'sum_', 'count_', 'min_', 'max_', 'none_', 'first_'];
  for (const prefix of aggregatePrefixes) {
    if (lowerAccessorKey.startsWith(prefix)) {
      const strippedKey = accessorKey.slice(prefix.length);
      const matchedKey = findAccessorKey(row, strippedKey);
      if (matchedKey) return matchedKey;
    }
  }

  const normalizedAccessorKey = normalizeAccessorToken(accessorKey);
  return rowKeys.find(key => {
    const normalizedKey = normalizeAccessorToken(key);
    return normalizedKey === normalizedAccessorKey
      || normalizedAccessorKey.startsWith(normalizedKey)
      || normalizedKey.startsWith(normalizedAccessorKey);
  });
};

export const AdvancedTable: React.FC<AdvancedTableProps> = ({
  component,
  mobileViewport = false,
}) => {
  const { properties, style } = component;
  const styleRecord = getStyleRecord(style);

  // Strip width (handled by ComponentWrapper) AND typography (table has its own Tailwind type system)
  const {
    width: _w, minWidth: _mw, maxWidth: _xw,
    fontSize: _fs, fontWeight: _fw, fontFamily: _ff, fontStyle: _fst,
    color: _clr, letterSpacing: _ls, lineHeight: _lh,
    textTransform: _tt, textDecoration: _td, textAlign: _ta,
    ...visualStyleRecord
  } = styleRecord ?? {};
  const visualStyle = extractTableContainerStyle(visualStyleRecord);
  const resolvedVisualStyle = mobileViewport
    ? normalizeMobileAdvancedTableStyle(visualStyle)
    : visualStyle;
  const mobileResolvedBackgroundColor = mobileViewport
    ? typeof visualStyle.backgroundColor === "string"
      ? resolveMobileAdvancedTableColorString(visualStyle.backgroundColor)
      : typeof visualStyle.background === "string"
        ? resolveMobileAdvancedTableColorString(visualStyle.background)
        : undefined
    : undefined;
  const mobileCardBackgroundColor =
    typeof mobileResolvedBackgroundColor === "string" &&
    mobileResolvedBackgroundColor.startsWith("#")
      ? mobileResolvedBackgroundColor
      : undefined;

  // Card styling: add card chrome if component.style doesn't already set a background
  const hasCustomBackground = !!(resolvedVisualStyle.background || resolvedVisualStyle.backgroundColor);
  const cardPaddingClasses =
    resolvedVisualStyle.padding === undefined ? (mobileViewport ? "p-3" : "p-4") : "";
  const cardRadiusClass = mobileViewport ? "rounded-2xl" : "rounded-lg";
  const cardClasses = mobileViewport
    ? `w-full h-full flex flex-col overflow-hidden border ${cardRadiusClass} ${cardPaddingClasses}`.trim()
    : hasCustomBackground
      ? `w-full h-full flex flex-col overflow-hidden ${cardPaddingClasses}`.trim()
      : `w-full h-full flex flex-col overflow-hidden border bg-card/50 border-border/50 shadow-sm ${cardRadiusClass} ${cardPaddingClasses}`.trim();
  const cardStyle: React.CSSProperties = {
    ...resolvedVisualStyle,
    ...(mobileViewport
      ? {
          ...(mobileCardBackgroundColor
            ? { backgroundColor: mobileCardBackgroundColor }
            : hasCustomBackground
              ? {}
              : { backgroundColor: mobileAdvancedTableColors.surfacePrimary }),
          borderColor:
            resolvedVisualStyle.borderColor ?? mobileAdvancedTableColors.border,
        }
      : {}),
    ...(resolvedVisualStyle.padding === undefined ? {} : { padding: resolvedVisualStyle.padding }),
  };

  // Data source extraction
  const dataSource = isQueryTemplateDataSource(properties?.dataSource)
    ? properties.dataSource
    : undefined;
  const templateId =
    typeof properties?.queryTemplateId === "string"
      ? properties.queryTemplateId
      : undefined;
  const hasQueryTemplate = isQueryTemplateDataSource(dataSource);
  const queryBindingEnabled = hasQueryTemplate || !!templateId;
  const staticData = React.useMemo(() => getTableRows(properties?.data), [properties?.data]);

  const {
    data: queryTemplateData,
    isLoading: qtLoading,
    error: qtError,
    refetch: qtRefetch,
    lastFetched: queryTemplateLastFetched,
  } = useQueryTemplateData({
    dataSource,
    componentId: component.id,
    enabled: queryBindingEnabled,
    templateId,
    initialData: queryBindingEnabled ? undefined : staticData,
  });
  const normalizedQueryTemplateData = React.useMemo(
    () => getTableRows(queryTemplateData),
    [queryTemplateData],
  );
  const hasLiveQueryTemplateData =
    queryBindingEnabled &&
    queryTemplateLastFetched !== null &&
    Array.isArray(queryTemplateData);

  const { data: storeData, loading: storeLoading, error: storeError } = useComponentData(component.id);
  const normalizedStoreData = React.useMemo(() => getTableRows(storeData), [storeData]);
  const loading = storeLoading || qtLoading;
  const error = storeError || qtError;

  // Auto-generate columns from data when properties.columns is not set
  const autoColumns: TableColumn[] = React.useMemo(() => {
    const resolvedData = queryBindingEnabled
      ? normalizedQueryTemplateData
      : normalizedStoreData.length > 0
        ? normalizedStoreData
        : normalizedQueryTemplateData.length > 0
          ? normalizedQueryTemplateData
          : staticData;

    if (!resolvedData || resolvedData.length === 0) return [];

    // Fields that are IDataRecord metadata (should never be user-facing columns)
    const metadataFields = new Set([
      '_id', 'recordId', 'record_id', 'documentId', 'document_id',
      '__v', 'subRows', 'schemaId', 'schemaVersion', 'groupId',
      'organizationId', 'metadata', 'createdAt', 'updatedAt',
    ]);

    return Object.keys(resolvedData[0])
      .filter(key => !metadataFields.has(key))
      .filter(key => {
        // Skip complex nested objects (e.g. un-flattened .data or .metadata)
        const val = resolvedData[0][key];
        return val === null || val === undefined || typeof val !== 'object'
          || val instanceof Date || Array.isArray(val);
      })
      .map(key => ({
        id: key,
        accessorKey: key,
        header: key
          // Strip unambiguous aggregation prefixes for cleaner display.
          // Keep first_/last_ because they are common data field names (first_name/last_name).
          .replace(/^(avg|sum|count|min|max|none)_/, '')
          .replace(/_/g, ' ')
          .replace(/([A-Z])/g, ' $1')
          .replace(/\b\w/g, s => s.toUpperCase())
          .trim(),
        enableSorting: true,
        enableColumnFilter: true,
      }));
  }, [
    normalizedStoreData,
    normalizedQueryTemplateData,
    queryBindingEnabled,
    staticData,
  ]);

  // Visual property extraction (same pattern as BasicTable)
  const isDashboardMode = properties?.dashboardMode === true;
  const rowSpacing = (typeof properties?.rowSpacing === 'string' ? properties.rowSpacing : isDashboardMode ? "tight" : "normal");
  const rowPadding = (typeof properties?.rowPadding === 'string' ? properties.rowPadding : isDashboardMode ? "compact" : "normal");
  const headerStyle = (typeof properties?.headerStyle === 'string' ? properties.headerStyle : isDashboardMode ? "typography" : "background");
  const effectiveHeaderStyle =
    mobileViewport && typeof properties?.headerStyle !== 'string'
      ? "typography"
      : headerStyle;
  const showBorders = properties?.showBorders !== false;
  const headerBgColor = typeof properties?.headerBackgroundColor === 'string' ? properties.headerBackgroundColor : undefined;
  const headerTxtColor = typeof properties?.headerTextColor === 'string' ? properties.headerTextColor : undefined;
  const bodyTextColor = typeof properties?.bodyTextColor === 'string' ? properties.bodyTextColor : undefined;
  const alternateRowColor = typeof properties?.alternateRowColor === 'string' ? properties.alternateRowColor : undefined;
  const rowHoverColor = typeof properties?.rowHoverColor === 'string' ? properties.rowHoverColor : undefined;
  const tableBorderColor = typeof properties?.borderColor === 'string' ? properties.borderColor : undefined;
  const stickyHeaderBackgroundStyle: React.CSSProperties = headerBgColor
    ? { backgroundColor: headerBgColor }
    : mobileViewport
      ? { backgroundColor: mobileCardBackgroundColor ?? mobileAdvancedTableColors.surfacePrimary }
      : resolvedVisualStyle.backgroundColor !== undefined
        ? { backgroundColor: resolvedVisualStyle.backgroundColor }
        : resolvedVisualStyle.background !== undefined
          ? { background: resolvedVisualStyle.background }
          : { backgroundColor: "hsl(var(--card))" };
  const configuredColumns = React.useMemo(
    () => getTableColumns(properties?.columns),
    [properties?.columns],
  );
  const baseColumns = configuredColumns.length > 0
    ? configuredColumns
    : autoColumns.length > 0
      ? autoColumns
      : defaultColumns;
  const visibleColumns = React.useMemo(
    () => getVisibleTableColumns(baseColumns, properties),
    [baseColumns, properties],
  );
  const canEditLocalRows = canMutateLocalTableRows(component);
  const tableWritebackConfig = React.useMemo(
    () => getTableRowWritebackConfig(component),
    [component],
  );
  const {
    createRecord,
    updateRecord,
    deleteRecord,
    resolveWriteTarget,
  } = useDataWriteback({
    writebackConfig: tableWritebackConfig,
    componentId: component.id,
  });
  const canCreateSourceRows = resolveWriteTarget("create").status === "ready";
  const canUpdateSourceRows = resolveWriteTarget("update").status === "ready";
  const canDeleteSourceRows = resolveWriteTarget("delete").status === "ready";
  const canMutateSourceRows =
    tableWritebackConfig !== undefined &&
    (canCreateSourceRows || canUpdateSourceRows || canDeleteSourceRows);
  const canOpenRowEditor = baseColumns.length > 0;
  const canEditRows = canEditLocalRows || canUpdateSourceRows;
  const canInteractWithRows = canOpenRowEditor;
  const [localRowsOverride, setLocalRowsOverride] = React.useState<TableRowData[] | null>(null);
  const [rowEditorState, setRowEditorState] = React.useState<RowEditorState | null>(null);
  const {
    enabled: tableModalPreviewEnabled,
    closePreview: closeTableModalPreview,
    openPreview: openTableModalPreview,
  } = useTableModalPreview();

  React.useEffect(() => {
    setLocalRowsOverride(null);
    setRowEditorState(null);
  }, [component.id, properties?.data]);

  const resolvedTableData = React.useMemo(() => {
    const sourceRows = hasLiveQueryTemplateData
      ? normalizedQueryTemplateData
      : queryBindingEnabled
        ? defaultData
        : normalizedStoreData.length > 0
        ? normalizedStoreData
        : normalizedQueryTemplateData.length > 0
          ? normalizedQueryTemplateData
          : staticData.length > 0
            ? staticData
            : defaultData;

    return (canEditLocalRows || canMutateSourceRows) && localRowsOverride !== null
      ? localRowsOverride
      : sourceRows;
  }, [
    canEditLocalRows,
    canMutateSourceRows,
    hasLiveQueryTemplateData,
    localRowsOverride,
    normalizedQueryTemplateData,
    normalizedStoreData,
    queryBindingEnabled,
    staticData,
  ]);
  const canAddLocalRows =
    (canEditLocalRows || canCreateSourceRows) &&
    properties?.dataAdditionsEnabled === true &&
    baseColumns.length > 0;

  // Row spacing classes (respects showBorders toggle)
  const getRowSpacingClass = (spacing: string) => {
    if (!showBorders) return spacing === "loose" ? "py-1" : "";
    switch (spacing) {
      case "none": return "";
      case "tight": return "border-b";
      case "loose": return "border-b-2 py-1";
      default: return "border-b";
    }
  };

  // Row padding classes
  const getRowPaddingClass = (padding: string) => {
    switch (padding) {
      case "compact": return "px-2 py-1";
      case "spacious": return mobileViewport ? "px-3 py-1.5" : "px-6 py-3";
      default: return mobileViewport ? "px-3 py-1.5" : "px-4 py-2";
    }
  };

  const tableConfig: TableConfig = {
    columns: visibleColumns,
    data: resolvedTableData,
    enableSorting: properties.enableSorting !== false,
    enableFiltering: properties.enableFiltering !== false,
    enableGlobalFilter: properties.enableGlobalFilter !== false,
    enableColumnResizing: properties.enableColumnResizing !== false,
    enableRowSelection: properties.enableRowSelection !== false,
    enablePagination: properties.enablePagination !== false,
    enableVirtualization: properties.enableVirtualization !== false,
    pageSize: typeof properties.pageSize === 'number' ? properties.pageSize : 50,
    maxHeight: typeof properties.maxHeight === 'number' ? properties.maxHeight : 600,
    striped: properties.striped !== false,
    hoverable: properties.hoverable !== false,
  };
  // Resolve a cell value from a row using fallback strategies
  const resolveAccessor = React.useCallback((row: Record<string, unknown>, accessorKey: string) => {
    // 1. Direct match (works for flat data and auto-columns)
    const directKey = findAccessorKey(row, accessorKey);
    if (directKey) return row[directKey];
    // 3. Aggregation-prefixed variants (handles queryTemplateData results)
    const prefixes = ['last_', 'avg_', 'sum_', 'count_', 'min_', 'max_', 'none_', 'first_'];
    for (const prefix of prefixes) {
      const key = `${prefix}${accessorKey}`;
      const prefixKey = findAccessorKey(row, key);
      if (prefixKey) return row[prefixKey];
    }
    // 4. Nested .data fallback (safety net for un-flattened IDataRecord)
    if (isRecord(row.data)) {
      const nestedKey = findAccessorKey(row.data, accessorKey);
      if (nestedKey) return row.data[nestedKey];
    }
    return undefined;
  }, []);

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [globalFilter, setGlobalFilter] = React.useState<string>("");
  const [expanded, setExpanded] = React.useState<ExpandedState>({});
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});

  const parentRef = React.useRef<HTMLDivElement>(null);

  const columns = React.useMemo(
    (): ColumnDef<TableRowData, unknown>[] =>
      tableConfig.columns.map((col) => {
        if (col.id === "select" && tableConfig.enableRowSelection) {
          return {
            id: "select",
            header: ({ table }: HeaderContext<TableRowData, unknown>) => (
              <Checkbox
                checked={table.getIsAllPageRowsSelected()}
                onCheckedChange={(value) =>
                  table.toggleAllPageRowsSelected(!!value)
                }
                aria-label="Select all"
              />
            ),
            cell: ({ row }: CellContext<TableRowData, unknown>) => (
              <Checkbox
                checked={row.getIsSelected()}
                onCheckedChange={(value) => row.toggleSelected(!!value)}
                aria-label="Select row"
              />
            ),
            enableSorting: false,
            enableHiding: false,
            size: 50,
          };
        }

        let customCell:
          | ColumnDef<TableRowData, unknown>["cell"]
          | undefined;

        if (col.accessorKey.toLowerCase() === "actions") {
          customCell = ({ row }: CellContext<TableRowData, unknown>) => {
            const actionsValue = resolveAccessor(row.original, col.accessorKey);
            return (
              <TableActionsCell
                tableId={component.id}
                rowId={getRowId(row.original, row.id)}
                rowData={row.original}
                actions={typeof actionsValue === "string" ? actionsValue : ""}
              />
            );
          };
        }

        return {
          // Use accessorFn for flexible key resolution (handles aggregation prefixes, nested data)
          id: col.id || col.accessorKey,
          accessorFn: (row: Record<string, unknown>) => resolveAccessor(row, col.accessorKey),
          header: col.header,
          enableSorting: col.enableSorting !== false,
          enableColumnFilter: col.enableColumnFilter !== false,
          enableResizing: col.enableResizing !== false,
          size: col.size || 150,
          minSize: col.minSize || 50,
          maxSize: col.maxSize || 500,
          cell:
            customCell ??
            (({ getValue }: CellContext<TableRowData, unknown>) => {
              const value = getValue();
              if (value === null || value === undefined) return "";
              if (value instanceof Date) return value.toLocaleDateString();
              if (typeof value === "object") return JSON.stringify(value);
              return String(value);
            }),
        };
      }),
    [tableConfig.columns, tableConfig.enableRowSelection, component.id, resolveAccessor],
  );

  const table = useReactTable<TableRowData>({
    data: tableConfig.data,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      expanded,
      rowSelection,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onExpandedChange: setExpanded,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    enableColumnResizing: tableConfig.enableColumnResizing,
    columnResizeMode: "onChange",
    getSubRows: (row) => row.subRows,
    initialState: {
      pagination: {
        pageSize: tableConfig.pageSize,
      },
    },
  });

  const { rows } = table.getRowModel();

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 35,
    overscan: 10,
    enabled: tableConfig.enableVirtualization,
  });

  const virtualRows = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const shouldRenderVirtualRows =
    tableConfig.enableVirtualization && virtualRows.length > 0;
  const renderRows = shouldRenderVirtualRows
    ? virtualRows
    : rows.map((_, index) => ({ index }));
  const filteredRowCount = table.getFilteredRowModel().rows.length;
  const currentPageIndex = table.getState().pagination.pageIndex;
  const currentPageSize = table.getState().pagination.pageSize;
  const displayPageCount = Math.max(table.getPageCount(), 1);
  const firstVisibleResult =
    filteredRowCount === 0 ? 0 : currentPageIndex * currentPageSize + 1;
  const lastVisibleResult =
    filteredRowCount === 0
      ? 0
      : Math.min((currentPageIndex + 1) * currentPageSize, filteredRowCount);
  const tableWidthStyle: TableStyle = mobileViewport
    ? {}
    : {
      '--tm-table-min-width': `${table.getCenterTotalSize()}px`,
    };
  const getColumnWidthStyle = (
    width: number,
  ): TableStyle => mobileViewport ? {} : ({
    '--tm-column-width': `${width}px`,
  });

  const paddingTop = shouldRenderVirtualRows ? virtualRows?.[0]?.start || 0 : 0;
  const paddingBottom =
    shouldRenderVirtualRows
      ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0)
      : 0;
  const rowEditorFields = React.useMemo(
    () =>
      createTableRowEditorFields(
        baseColumns,
        resolvedTableData,
        rowEditorState?.rowData ?? {},
      ),
    [baseColumns, resolvedTableData, rowEditorState],
  );

  const commitResolvedRows = React.useCallback(
    (nextRows: TableRowData[]) => {
      if (!canEditLocalRows && !canMutateSourceRows) return;

      setLocalRowsOverride(nextRows);
      if (canEditLocalRows) {
        commitLocalTableRows(component, nextRows);
      }
    },
    [canEditLocalRows, canMutateSourceRows, component],
  );

  const openCreateRowEditor = React.useCallback(() => {
    if (!canAddLocalRows) return;

    setRowEditorState({
      mode: "create",
      rowData: { id: generateLocalTableRowId() },
    });
  }, [canAddLocalRows]);

  const openEditRowEditor = React.useCallback(
    (rowIndex: number, rowData: TableRowData) => {
      if (import.meta.env.DEV) {
        console.debug("[AdvancedTable] open row editor requested", {
          componentId: component.id,
          rowIndex,
          canOpenRowEditor,
          columnCount: baseColumns.length,
          rowKeys: Object.keys(rowData),
        });
      }

      if (!canOpenRowEditor) return;

      setRowEditorState({
        mode: "edit",
        rowIndex,
        rowData,
      });
    },
    [baseColumns.length, canOpenRowEditor, component.id],
  );

  const handleTableRowClickCapture = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const target = event.target instanceof HTMLElement ? event.target : null;
      const interactiveTarget = isInteractiveTableTarget(event.target);
      if (interactiveTarget) {
        if (import.meta.env.DEV) {
          console.debug("[AdvancedTable] row click capture ignored", {
            componentId: component.id,
            targetTag: target?.tagName,
            reason: "interactive-target",
          });
        }
        return;
      }

      const rowPosition = getDelegatedTableRowIndex(event.target);
      if (rowPosition === null) {
        if (import.meta.env.DEV) {
          console.debug("[AdvancedTable] row click capture ignored", {
            componentId: component.id,
            targetTag: target?.tagName,
            reason: "missing-row-index",
          });
        }
        return;
      }

      const row = rows[rowPosition];
      if (!row) {
        if (import.meta.env.DEV) {
          console.debug("[AdvancedTable] row click capture ignored", {
            componentId: component.id,
            targetTag: target?.tagName,
            rowPosition,
            rowCount: rows.length,
            reason: "missing-row",
          });
        }
        return;
      }

      if (import.meta.env.DEV) {
        console.debug("[AdvancedTable] row click capture", {
          componentId: component.id,
          targetTag: target?.tagName,
          rowPosition,
          rowIndex: row.index,
          canOpenRowEditor,
          columnCount: baseColumns.length,
        });
      }

      event.stopPropagation();
      openEditRowEditor(row.index, row.original);
    },
    [baseColumns.length, canOpenRowEditor, component.id, openEditRowEditor, rows],
  );

  const getEditablePayload = React.useCallback(
    (row: Record<string, unknown>) => {
      const editableKeys = new Set(rowEditorFields.map((field) => field.key));
      const payload: Record<string, unknown> = {};

      Object.entries(row).forEach(([key, value]) => {
        if (editableKeys.has(key)) {
          payload[key] = value;
        }
      });

      return payload;
    },
    [rowEditorFields],
  );

  const saveRowEditor = React.useCallback(
    async (nextRow: Record<string, unknown>): Promise<boolean> => {
      if (!rowEditorState) return false;

      if (rowEditorState.mode === "create") {
        const nextRowId =
          typeof nextRow.id === "string" && nextRow.id.length > 0
            ? nextRow.id
            : generateLocalTableRowId();
        const rowWithId: TableRowData = { ...nextRow, id: nextRowId };
        const nextRows = [...resolvedTableData, rowWithId];
        if (canEditLocalRows) {
          commitResolvedRows(nextRows);
          setRowEditorState(null);
          return true;
        }

        const didCreate = await createRecord(getEditablePayload(nextRow), {
          metadata: { source: "table", tags: ["table"] },
          onOptimisticUpdate: () => commitResolvedRows(nextRows),
          onRollback: () => setLocalRowsOverride(resolvedTableData),
        });
        if (didCreate) {
          setRowEditorState(null);
          return true;
        }
        return false;
      } else {
        const nextRows = resolvedTableData.map((row, index) =>
          index === rowEditorState.rowIndex
            ? { ...row, ...nextRow }
            : row,
        );
        if (canEditLocalRows) {
          commitResolvedRows(nextRows);
          setRowEditorState(null);
          return true;
        }

        const recordId = getTableRowRecordId(rowEditorState.rowData);
        if (recordId === undefined) return false;

        const didUpdate = await updateRecord(recordId, getEditablePayload(nextRow), {
          onOptimisticUpdate: () => commitResolvedRows(nextRows),
          onRollback: () => setLocalRowsOverride(resolvedTableData),
        });
        if (didUpdate) {
          setRowEditorState(null);
          return true;
        }
        return false;
      }
    },
    [
      canEditLocalRows,
      commitResolvedRows,
      createRecord,
      getEditablePayload,
      resolvedTableData,
      rowEditorState,
      updateRecord,
    ],
  );

  const deleteEditedRow = React.useCallback(async () => {
    if (!rowEditorState || rowEditorState.mode !== "edit") return false;

    const nextRows = resolvedTableData.filter((_, index) => index !== rowEditorState.rowIndex);
    if (canEditLocalRows) {
      commitResolvedRows(nextRows);
      setRowEditorState(null);
      return true;
    }

    const recordId = getTableRowRecordId(rowEditorState.rowData);
    if (recordId === undefined) return false;

    const didDelete = await deleteRecord(recordId, {
      onOptimisticUpdate: () => commitResolvedRows(nextRows),
      onRollback: () => setLocalRowsOverride(resolvedTableData),
    });
    if (didDelete) {
      setRowEditorState(null);
      return true;
    }
    return false;
  }, [
    canEditLocalRows,
    commitResolvedRows,
    deleteRecord,
    resolvedTableData,
    rowEditorState,
  ]);

  React.useEffect(() => {
    if (!tableModalPreviewEnabled || !rowEditorState) return;

    openTableModalPreview({
      id:
        rowEditorState.mode === "create"
          ? `${component.id}:create`
          : `${component.id}:edit:${rowEditorState.rowIndex}`,
      componentId: component.id,
      componentName: component.name || "Table",
      mode: rowEditorState.mode,
      fields: rowEditorFields,
      rowData: rowEditorState.rowData,
      mobileViewport,
      readOnly: rowEditorState.mode === "edit" && !canEditRows,
      onClose: () => setRowEditorState(null),
      onSave: async (nextRow) => {
        const didSave = await saveRowEditor(nextRow);
        if (didSave) closeTableModalPreview();
      },
      onDelete:
        rowEditorState.mode === "edit" && (canEditLocalRows || canDeleteSourceRows)
          ? async () => {
              const didDelete = await deleteEditedRow();
              if (didDelete) closeTableModalPreview();
            }
          : undefined,
    });
  }, [
    canDeleteSourceRows,
    canEditLocalRows,
    canEditRows,
    closeTableModalPreview,
    component.id,
    component.name,
    deleteEditedRow,
    mobileViewport,
    openTableModalPreview,
    rowEditorFields,
    rowEditorState,
    saveRowEditor,
    tableModalPreviewEnabled,
  ]);

  if (loading) {
    return (
      <div className={cardClasses} style={cardStyle}>
        {typeof properties?.title === 'string' && properties.title && (
          <div className="mb-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{properties.title}</h3>
            {typeof properties?.description === 'string' && properties.description && (
              <p className="text-sm text-muted-foreground">{properties.description}</p>
            )}
          </div>
        )}
        <div className="flex-1 min-h-[200px]">
          <TableLoadingSkeleton />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cardClasses} style={cardStyle}>
        {typeof properties?.title === 'string' && properties.title && (
          <div className="mb-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{properties.title}</h3>
            {typeof properties?.description === 'string' && properties.description && (
              <p className="text-sm text-muted-foreground">{properties.description}</p>
            )}
          </div>
        )}
        <div className="flex flex-col items-center justify-center flex-1 min-h-[200px] space-y-2">
          <AlertCircle className="w-8 h-8 text-destructive" />
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="outline" size="sm" onClick={() => qtRefetch()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (
    tableConfig.data.length === 0 &&
    !loading &&
    !error &&
    !canAddLocalRows &&
    (!mobileViewport || tableConfig.columns.length === 0)
  ) {
    return (
      <div className={cardClasses} style={cardStyle}>
        {typeof properties?.title === 'string' && properties.title && (
          <div className="mb-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{properties.title}</h3>
            {typeof properties?.description === 'string' && properties.description && (
              <p className="text-sm text-muted-foreground">{properties.description}</p>
            )}
          </div>
        )}
        <div className="flex flex-col items-center justify-center flex-1 min-h-[200px] text-muted-foreground">
          <p className="text-sm">No data to display</p>
          <p className="text-xs mt-1">Connect a data source or add data to this table</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cardClasses} style={cardStyle}>
      {typeof properties.title === 'string' && properties.title && (
        <div className="mb-4">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{properties.title}</h3>
          {typeof properties.description === 'string' && properties.description && (
            <p className="text-sm text-muted-foreground">
              {properties.description}
            </p>
          )}
        </div>
      )}

      {/* Controls */}
      <div
        className={`flex items-center justify-between ${
          mobileViewport ? "mb-3 gap-3" : "mb-4 space-x-4"
        }`}
      >
        {/* Global Filter */}
        {tableConfig.enableGlobalFilter && (
          <Input
            placeholder="Search all columns..."
            value={globalFilter ?? ""}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className={
              mobileViewport
                ? "min-w-0 flex-1 max-w-none border-[#363B47] bg-[#272B34] text-[#ECEEF2] placeholder:text-[#8A93A5]"
              : "max-w-sm"
            }
          />
        )}
        {!tableConfig.enableGlobalFilter && <div />}

        <div className="flex shrink-0 items-center gap-2">
          {/* Selection Info */}
          {!mobileViewport &&
            tableConfig.enableRowSelection &&
            Object.keys(rowSelection).length > 0 && (
              <div className="text-xs text-muted-foreground">
                {Object.keys(rowSelection).length} of{" "}
                {table.getFilteredRowModel().rows.length} row(s) selected
              </div>
            )}
          {canAddLocalRows ? (
            <Button
              type="button"
              size="icon"
              variant="outline"
              aria-label="New row"
              title="New row"
              onClick={openCreateRowEditor}
              className="h-9 w-9 shrink-0 rounded-lg border-[#1CA9CC]/50 bg-[#1CA9CC]/10 p-0 text-[#D9F7FF] shadow-none hover:border-[#1CA9CC] hover:bg-[#1CA9CC]/15 hover:text-white focus-visible:ring-[#1CA9CC]/40 focus-visible:ring-offset-0"
            >
              <Plus aria-hidden="true" className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      </div>

      {/* Table */}
      <div
        className={`flex-1 overflow-hidden ${
          mobileViewport ? "border-0 rounded-none" : "border rounded-md"
        }`}
        data-table-renderer-kind="advanced"
        style={{
          fontSize: getFontSize(properties?.fontSize),
          ...(rowHoverColor ? { '--tm-row-hover': rowHoverColor } : {}),
        }}
      >
        <div
          ref={parentRef}
          className="overflow-auto"
          style={{ maxHeight: tableConfig.maxHeight }}
        >
          <table
            className={`w-full text-sm ${
              mobileViewport ? "min-w-full" : "min-w-[var(--tm-table-min-width)]"
            }`}
            style={tableWidthStyle}
          >
            <thead
              className="sticky top-0 z-10"
              style={stickyHeaderBackgroundStyle}
            >
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={`h-12 text-left align-middle ${
                        effectiveHeaderStyle === 'typography'
                          ? `font-semibold ${mobileViewport ? 'text-xs uppercase tracking-wider text-[#9AA2B1]' : 'text-foreground'}`
                          : headerTxtColor
                            ? `font-medium ${mobileViewport ? 'text-xs uppercase tracking-wider' : ''}`
                            : `font-medium ${mobileViewport ? 'text-xs uppercase tracking-wider text-[#9AA2B1]' : 'text-muted-foreground'}`
                      } ${showBorders ? `border-b ${mobileViewport ? 'border-[#363B47]' : ''}` : ''} ${getRowPaddingClass(rowPadding)} relative ${mobileViewport ? 'w-auto' : 'w-[var(--tm-column-width)]'}`}
                      style={{
                        ...stickyHeaderBackgroundStyle,
                        ...getColumnWidthStyle(header.getSize()),
                        ...(headerTxtColor ? { color: headerTxtColor } : {}),
                      }}
                    >
                      <div className="flex items-center space-x-2">
                        <div
                          className={
                            header.column.getCanSort()
                              ? "cursor-pointer select-none"
                              : ""
                          }
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </div>
                        {header.column.getCanSort() && (
                          <div className="flex flex-col">
                            {header.column.getIsSorted() === "asc" && (
                              <ChevronUp className="h-3 w-3" />
                            )}
                            {header.column.getIsSorted() === "desc" && (
                              <ChevronDown className="h-3 w-3" />
                            )}
                          </div>
                        )}
                      </div>

                      {/* Column Filter */}
                      {!mobileViewport &&
                        header.column.getCanFilter() &&
                        tableConfig.enableFiltering && (
                          <div className="mt-1">
                            <Input
                              placeholder={`Filter...`}
                              value={getFilterInputValue(header.column.getFilterValue())}
                              onChange={(e) =>
                                header.column.setFilterValue(e.target.value)
                              }
                              className="h-8 text-xs"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        )}

                      {/* Column Resizer */}
                      {!mobileViewport &&
                        header.column.getCanResize() &&
                        tableConfig.enableColumnResizing && (
                          <div
                            className="absolute top-0 right-0 w-1 h-full bg-border cursor-col-resize select-none touch-none opacity-0 hover:opacity-100"
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                          />
                        )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody onClickCapture={handleTableRowClickCapture}>
              {paddingTop > 0 && (
                <tr>
                  <td
                    colSpan={table.getVisibleLeafColumns().length || 1}
                    style={{ height: `${paddingTop}px` }}
                  />
                </tr>
              )}
              {renderRows.map((virtualRow) => {
                const row = rows[virtualRow.index];
                return (
                  <tr
                    key={row.id}
                    data-table-row-index={virtualRow.index}
                    className={`${getRowSpacingClass(rowSpacing)} ${
                      tableConfig.striped && virtualRow.index % 2 === 1 && !alternateRowColor ? 'bg-muted/25' : ''
                    } ${mobileViewport && showBorders ? 'border-[#363B47]' : ''} ${tableConfig.hoverable ? (rowHoverColor ? 'hover:bg-[var(--tm-row-hover)]' : 'hover:bg-muted/50') : ''} ${canInteractWithRows ? 'cursor-pointer' : ''}`}
                    style={{
                      ...(tableConfig.striped && virtualRow.index % 2 === 1 && alternateRowColor ? { backgroundColor: alternateRowColor } : {}),
                      ...(tableBorderColor ? { borderBottomColor: tableBorderColor } : {}),
                    }}
                    tabIndex={canInteractWithRows ? 0 : undefined}
                    onClick={(event) => {
                      if (isInteractiveTableTarget(event.target)) return;
                      event.stopPropagation();
                      openEditRowEditor(row.index, row.original);
                    }}
                    onKeyDown={(event) => {
                      if (!canInteractWithRows) return;
                      if (event.key !== "Enter" && event.key !== " ") return;
                      if (isInteractiveTableTarget(event.target)) return;
                      event.preventDefault();
                      event.stopPropagation();
                      openEditRowEditor(row.index, row.original);
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className={`align-middle ${getRowPaddingClass(rowPadding)} ${mobileViewport ? 'w-auto' : 'w-[var(--tm-column-width)]'}`}
                        style={{
                          ...getColumnWidthStyle(cell.column.getSize()),
                          ...(bodyTextColor
                            ? { color: bodyTextColor }
                            : mobileViewport
                              ? { color: mobileAdvancedTableColors.textSecondary }
                              : {}),
                        }}
                      >
                        <div className="flex items-center space-x-2">
                          {/* Expand/Collapse Button */}
                          {cell.column.id === "name" && row.getCanExpand() && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={row.getToggleExpandedHandler()}
                              className="p-0 h-6 w-6"
                            >
                              {row.getIsExpanded() ? (
                                <ChevronDownIcon className="h-4 w-4" />
                              ) : (
                                <ChevronRightIcon className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                          <div>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </div>
                        </div>
                      </td>
                    ))}
                  </tr>
                );
              })}
              {paddingBottom > 0 && (
                <tr>
                  <td
                    colSpan={table.getVisibleLeafColumns().length || 1}
                    style={{ height: `${paddingBottom}px` }}
                  />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {tableConfig.enablePagination && (
        <div
          className={`flex ${
            mobileViewport
              ? "flex-col items-start gap-2 pt-2"
              : "items-center justify-between space-x-2 py-4"
          }`}
        >
          <div className={mobileViewport ? "text-xs text-[#8A93A5]" : "text-xs text-muted-foreground"}>
            Showing{" "}
            {firstVisibleResult}{" "}
            to{" "}
            {lastVisibleResult}{" "}
            of {filteredRowCount} results
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
              className={mobileViewport ? "hidden" : undefined}
            >
              {"<<"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className={
                mobileViewport
                  ? "h-8 w-8 px-0 border-[#363B47] bg-[#1C1F26] text-[#9AA2B1] disabled:text-[#8892A3]"
                  : undefined
              }
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className={mobileViewport ? "text-xs text-[#9AA2B1]" : "text-sm"}>
              Page {table.getState().pagination.pageIndex + 1} of{" "}
              {displayPageCount}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className={
                mobileViewport
                  ? "h-8 w-8 px-0 border-[#363B47] bg-[#1C1F26] text-[#9AA2B1] disabled:text-[#8892A3]"
                  : undefined
              }
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.setPageIndex(Math.max(table.getPageCount() - 1, 0))}
              disabled={!table.getCanNextPage()}
              className={mobileViewport ? "hidden" : undefined}
            >
              {">>"}
            </Button>
          </div>
        </div>
      )}
      {!tableModalPreviewEnabled && (
        <TableRowDataEditor
          open={rowEditorState !== null}
          mode={rowEditorState?.mode ?? "edit"}
          fields={rowEditorFields}
          rowData={rowEditorState?.rowData ?? {}}
          mobileViewport={mobileViewport}
          readOnly={rowEditorState?.mode === "edit" && !canEditRows}
          onOpenChange={(open) => {
            if (!open) setRowEditorState(null);
          }}
          onSave={saveRowEditor}
          onDelete={
            rowEditorState?.mode === "edit" && (canEditLocalRows || canDeleteSourceRows)
              ? deleteEditedRow
              : undefined
          }
        />
      )}
    </div>
  );
};
